import { call, put, delay, select, takeLeading, takeLatest } from 'redux-saga/effects';

import Ondemand from 'services/ondemand';
import { getProductConfig } from 'configs/products';
import {
  fetchOndemandRequested,
  fetchOndemandFailed,
  fetchOndemandSucceeded,
  createOndemandRequested,
  createOndemandFailed,
  createOndemandSucceeded,
  listOndemandRequested,
  listOndemandSucceeded,
  listOndemandFailed,
  decommissionOndemandRequested,
  decommissionOndemandSucceeded,
  decommissionOndemandFailed,
  selectOndemandSite
} from './ondemandSlice';


// Backend-only products never report a front end pipeline status.
const buildsFrontend = (product) =>
  getProductConfig(product)?.hasFrontend !== false;

const pipelinesSucceeded = (data, product) =>
  data.be_pipeline_status === 'Succeeded'
  && (!buildsFrontend(product) || data.fe_pipeline_status === 'Succeeded');

const pipelinesFailed = (data, product) =>
  data.be_pipeline_status === 'Failed'
  || (buildsFrontend(product) && data.fe_pipeline_status === 'Failed');

export function* handleFetchOndemand() {
  try {
    const ondemandSite = yield select(selectOndemandSite);
    if(ondemandSite) {
      const { data } = yield call(Ondemand.get, ondemandSite.SK, ondemandSite.product);
      const product = (data && data.product) || ondemandSite.product;
      if(data 
        && data.state === 'complete'
        && data.data_transfer_status === 'complete'
        && pipelinesSucceeded(data, product)
        ) {
        yield put(
          fetchOndemandSucceeded(data)
        );
        return;
      } else if(data) {
        if(data.state === 'error') {
          yield put(
            fetchOndemandFailed(`Ondemand Creation has failed! Error ${data.error}`)
          );
          return;
        } else if(pipelinesFailed(data, product)) {
          yield put(
            fetchOndemandFailed('Ondemand Creation has failed! Error: Pipeline execution has failed')
          );
          return;
        }
      }
    }
    yield delay(20000);

    yield put(
      fetchOndemandRequested()
    );
  } catch (error) {
    let response = error.response;
    let apiError;
    if (response.data) {
      apiError = response.data;
    } else {
      apiError = error.message;
    }
    yield put(fetchOndemandFailed(apiError));
  }
}

export function* handleCreateOndemand({ payload }) {
  try {
    const { data } = yield call(Ondemand.create, payload);
    yield put(
      createOndemandSucceeded(data),
    );
  } catch (error) {
    let response = error.response;
    let apiError;
    if (typeof response.data !== 'undefined' 
      && response.data) {
      apiError = response.data;
    } else {
      apiError = error.message;
    }
    yield put(createOndemandFailed(apiError));
  }
}

export function* handleListOndemand({ payload }) {
  const product = payload?.product;
  const includeDeleted = Boolean(payload?.includeDeleted);
  const params = {
    include_deleted: includeDeleted,
  };
  if (payload?.limit != null) {
    params.limit = payload.limit;
  }
  if (payload?.cursor) {
    params.cursor = payload.cursor;
  }

  try {
    const { data } = yield call(Ondemand.list, product, params);
    yield put(
      listOndemandSucceeded({
        product,
        sites: data?.sites ?? [],
        nextCursor: data?.next_cursor ?? null,
      }),
    );
  } catch (error) {
    const response = error.response;
    const apiError =
      response?.data !== undefined && response.data
        ? response.data
        : error.message;
    yield put(listOndemandFailed({ product, error: apiError }));
  }
}

export function* handleDecommissionOndemand({ payload }) {
  const product = payload?.product;
  const sk = payload?.sk;

  try {
    yield call(Ondemand.decommission, product, sk);
    yield put(decommissionOndemandSucceeded({ product, sk }));
    yield put(
      listOndemandRequested({
        product,
        includeDeleted: Boolean(payload?.includeDeleted),
      }),
    );
  } catch (error) {
    const response = error.response;
    const apiError =
      response?.data !== undefined && response.data
        ? response.data
        : error.message;
    yield put(decommissionOndemandFailed({ error: apiError }));
  }
}

const watchers = [
  takeLatest(fetchOndemandRequested.type, handleFetchOndemand),
  takeLeading(createOndemandRequested.type, handleCreateOndemand),
  takeLatest(listOndemandRequested.type, handleListOndemand),
  takeLeading(decommissionOndemandRequested.type, handleDecommissionOndemand),
];

export default watchers;
